import { plural, t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import type { BookingProposalRecordList } from '@navio/nsdk';
import { NoddiLink } from 'noddi-ui-web';
import { customerRoutes, formatCurrencyAmount } from 'noddi-util';
import { getProposalRowSummary } from './proposalRowSummary';
import { getProposalSummaryDecimals } from './proposalSummaryDecimals';

type Props = {
  proposal: BookingProposalRecordList;
};

/**
 * One pending proposal as a compact row: what is due, who drives the car, what it
 * costs, and the way to confirm it. Deliberately image-free so a customer with a
 * fleet of recalls can read the whole set instead of scrolling past a card each.
 */
export const ProposalRow = ({ proposal }: Props) => {
  const { carName, licensePlateNumber, extraVehicleCount, productNames, driverName, total } =
    getProposalRowSummary(proposal);

  const title = [carName, licensePlateNumber].filter(Boolean).join(' — ');
  const meta = [
    productNames.join(', '),
    extraVehicleCount > 0 ? plural(extraVehicleCount, { one: '+# more vehicle', other: '+# more vehicles' }) : null,
    driverName ? t`Driver: ${driverName}` : null
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className='flex flex-col gap-4 p-4 md:flex-row md:items-center md:justify-between md:gap-5 md:px-5'>
      <div className='flex flex-col gap-0.5'>
        <span className='font-semibold'>{title}</span>
        {meta && <span className='text-sm text-muted-foreground'>{meta}</span>}
      </div>
      <div className='flex items-center justify-between gap-4 md:justify-end'>
        <span className='font-semibold whitespace-nowrap'>
          {formatCurrencyAmount({ money: total, decimals: getProposalSummaryDecimals([total.amount]) })}
        </span>
        {/* Names what the page does, not what this control does: the row only opens the
            proposal, and a bare "Confirm" read as booking the car straight from the list. */}
        <NoddiLink
          to={customerRoutes.bookingProposalLandingPage.getPath({ slug: proposal.slug })}
          variant='primary'
          size='small'
          endIcon='ArrowRight'
        >
          <Trans>Review and confirm</Trans>
        </NoddiLink>
      </div>
    </div>
  );
};
